import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, BadgeCheck, ScanText, Loader2 } from "lucide-react";
import ApprovalWarningsBanner from "@/components/ApprovalWarningsBanner";
import { api, formatApiErrorDetail } from "@/lib/api";
import GermanDateInput from "@/components/GermanDateInput";

const TEXT = [["official_name", "Maßnahmebezeichnung (laut Zulassung)", 2], ["number", "Maßnahmenummer"], ["traeger_nummer", "Trägernummer"],
  ["ziel", "Maßnahme-Ziel (für die Trägerbestätigung)", 2],
  ["zertifikatsnummer", "Zertifikatsnummer"], ["fachkundige_stelle", "Fachkundige Stelle"], ["massnahmeform", "Maßnahmeform"]];
const PERIODS = [["massnahme", "Maßnahmezulassungszeitraum"], ["traeger", "Trägerzulassungszeitraum"], ["zeitraum", "Maßnahmezeitraum (Gesamtdauer)"]];

function ApprovalCard({ m, onSaved }) {
  const [a, setA] = useState(m.approval || {});
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const fileRef = useRef(null);
  const readDoc = async (file) => {
    setReading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/measure-approvals/extract", fd, { timeout: 120000 });
      setA((x) => ({ ...x, ...Object.fromEntries(Object.entries(r.data).filter(([, v]) => v)) }));
      toast.success("Bogen ausgelesen – bitte Werte prüfen und dann speichern");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setReading(false); }
  };
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const id = `${m.kind}-${m.measure_id}`;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/measure-approvals/${m.kind}/${m.measure_id}`, a); toast.success(`„${m.name}“ gespeichert`); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 bg-white p-5 space-y-4" data-testid={`approval-card-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{m.kind === "avgs" ? "AVGS-Coaching" : "Weiterbildung"}</span>
          <h3 className="text-base md:text-lg font-medium text-slate-900">{m.name}</h3>
        </div>
        <div className="flex items-center gap-3">
          {a.number && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><BadgeCheck size={14} /> Nr. {a.number}</span>}
          <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" data-testid={`approval-extract-input-${id}`}
            onChange={(e) => { if (e.target.files[0]) readDoc(e.target.files[0]); e.target.value = ""; }} />
          <button onClick={() => fileRef.current?.click()} disabled={reading} data-testid={`approval-extract-btn-${id}`}
            className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50">
            {reading ? <Loader2 size={13} className="animate-spin" /> : <ScanText size={13} />} {reading ? "Liest aus …" : "Aus Übersichtsbogen auslesen"}
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {TEXT.map(([k, l, span]) => (
          <label key={k} className={`text-xs text-slate-500 ${span ? "sm:col-span-2" : ""}`}>{l}
            <input value={a[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`approval-${id}-${k}`}
              className="mt-1 w-full border border-slate-200 px-2 py-1.5 text-sm text-slate-900" />
          </label>
        ))}
        {PERIODS.map(([k, l]) => (
          <div key={k} className="sm:col-span-2 text-xs text-slate-500">{l}
            <div className="mt-1 flex items-center gap-2" data-testid={`approval-${id}-${k}`}>
              <GermanDateInput value={a[`${k}_von`] || ""} onChange={(e) => set(`${k}_von`, e.target.value)} className="border border-slate-200 px-2 py-1.5 text-sm text-slate-900 w-36" />
              <span>bis</span>
              <GermanDateInput value={a[`${k}_bis`] || ""} onChange={(e) => set(`${k}_bis`, e.target.value)} className="border border-slate-200 px-2 py-1.5 text-sm text-slate-900 w-36" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid={`approval-save-${id}`}
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700 transition-colors disabled:opacity-50">
        <Save size={14} /> Speichern
      </button>
    </div>
  );
}

export default function MeasureApprovalsTab() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/measure-approvals").then((r) => setRows(r.data)).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  if (!rows) return <div className="text-xs text-slate-400 p-4">Lädt…</div>;
  return (
    <div className="space-y-4" data-testid="measure-approvals-tab">
      <ApprovalWarningsBanner showLink={false} />
      <p className="text-sm text-slate-500 max-w-2xl">Amtliche Angaben aus Übersichts-/Maßnahmebogen. Sie gelten für alle Module der Maßnahme und werden automatisch in jede Trägerbestätigung übernommen. Stunden und Kosten je Modul kommen aus dem Maßnahmen-Katalog bzw. den gebuchten Modulen des Kunden.</p>
      {rows.map((m) => <ApprovalCard key={`${m.kind}-${m.measure_id}`} m={m} onSaved={load} />)}
    </div>
  );
}
